import React from "react";
import { Link } from "react-router-dom";
import "./ShakeChillies.css";

const rapidApi = {
  company:
    "https://rapidapi.com/shake-chillies-shake-chillies-default/api/crunchbase4?utm_source=leadzlab&utm_medium=canonical_guide&utm_campaign=company_profile_enrichment",
  funding:
    "https://rapidapi.com/shake-chillies-shake-chillies-default/api/funding-tracker?utm_source=leadzlab&utm_medium=canonical_guide&utm_campaign=funding_tracker",
  team:
    "https://rapidapi.com/team/shake-chillies-shake-chillies-default?utm_source=leadzlab&utm_medium=shakechillies_home&utm_campaign=api_catalogue",
};

const siteUrl = "https://leadzlab.co";

function upsertMeta(selector, createAttrs, valueAttr, value) {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement(createAttrs.rel ? "link" : "meta");
    Object.entries(createAttrs).forEach(([key, attrValue]) => {
      tag.setAttribute(key, attrValue);
    });
    document.head.appendChild(tag);
  }
  tag.setAttribute(valueAttr, value);
}

function PageMeta({
  title,
  description,
  path,
  image = "https://storage.googleapis.com/leadzlab/MainLogo.png",
  type = "website",
  jsonLd,
}) {
  React.useEffect(() => {
    const url = `${siteUrl}${path}`;
    document.title = title;
    upsertMeta('meta[name="description"]', { name: "description" }, "content", description);
    upsertMeta('link[rel="canonical"]', { rel: "canonical" }, "href", url);
    upsertMeta('meta[property="og:title"]', { property: "og:title" }, "content", title);
    upsertMeta('meta[property="og:description"]', { property: "og:description" }, "content", description);
    upsertMeta('meta[property="og:url"]', { property: "og:url" }, "content", url);
    upsertMeta('meta[property="og:type"]', { property: "og:type" }, "content", type);
    upsertMeta('meta[property="og:image"]', { property: "og:image" }, "content", image);
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card" }, "content", "summary_large_image");
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title" }, "content", title);
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description" }, "content", description);
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image" }, "content", image);

    const scriptId = "shakechillies-jsonld";
    document.getElementById(scriptId)?.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      document.getElementById(scriptId)?.remove();
    };
  }, [description, image, jsonLd, path, title, type]);

  return null;
}

function articleJsonLd({ headline, description, image, path }) {
  return {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline,
    description,
    image,
    author: {
      "@type": "Organization",
      name: "ShakeChillies",
    },
    publisher: {
      "@type": "Organization",
      name: "LeadZLab",
      logo: {
        "@type": "ImageObject",
        url: "https://storage.googleapis.com/leadzlab/MainLogo.png",
      },
    },
    datePublished: "2026-07-12",
    dateModified: "2026-07-12",
    mainEntityOfPage: `${siteUrl}${path}`,
  };
}

const guides = [
  {
    title: "Enrich company profiles from public business data",
    description:
      "A practical walkthrough for turning a company name into structured fields like website, location, industries, size, and optional funding data.",
    href: "/shakechillies/guides/company-profile-enrichment",
    image: "/shakechillies/assets/company-profile-enrichment-cover.png",
    tag: "Company enrichment",
  },
  {
    title: "Track newly funded startups in 10 minutes",
    description:
      "Build a simple research or prospecting workflow from recent funding signals, stages, countries, investors, websites, and LinkedIn URLs.",
    href: "/shakechillies/guides/startup-funding-tracker",
    image: "/shakechillies/assets/funding-tracker-cover.png",
    tag: "Funding signals",
  },
];

function Header() {
  return (
    <header className="sc-nav">
      <Link className="sc-brand" to="/shakechillies">
        <span className="sc-brand-mark">
          <img
            src="/shakechillies/assets/shakechillies-avatar.png"
            alt=""
          />
        </span>
        <strong>ShakeChillies</strong>
      </Link>
      <nav>
        <Link to="/shakechillies">Home</Link>
        <Link to="/shakechillies/guides">Guides</Link>
        <Link to="/shakechillies/apis">APIs</Link>
      </nav>
    </header>
  );
}

function Footer() {
  return (
    <footer className="sc-footer">
      <p>A ShakeChillies project, hosted under LeadZLab.</p>
      <a
        href={rapidApi.team}
        target="_blank"
        rel="noopener noreferrer"
      >
        View ShakeChillies on RapidAPI
      </a>
    </footer>
  );
}

export function ShakeChilliesHome() {
  return (
    <main className="sc-page">
      <PageMeta
        title="ShakeChillies API Guides | LeadZLab"
        description="Developer-friendly ShakeChillies guides for company research, lead enrichment, funding-signal monitoring, and related automation workflows."
        path="/shakechillies"
      />
      <Header />
      <div
        className="sc-brand-ribbon"
        aria-hidden="true"
        style={{
          backgroundImage:
            'url("/shakechillies/assets/shakechillies-pattern.webp")',
        }}
      />
      <section className="sc-hero">
        <p className="sc-eyebrow">ShakeChillies · Practical API workflows</p>
        <h1>Developer-friendly guides for B2B data workflows.</h1>
        <p>
          ShakeChillies publishes small, focused APIs on RapidAPI. These guides
          show practical ways to use them for company research, lead enrichment,
          funding-signal monitoring, and related automation workflows.
        </p>
        <div className="sc-hero-actions">
          <Link to="/shakechillies/guides" className="sc-button primary">
            Browse guides
          </Link>
          <Link to="/shakechillies/apis" className="sc-button secondary">
            Browse APIs
          </Link>
        </div>
      </section>
      <Footer />
    </main>
  );
}

function CatalogueHeader({ eyebrow, title, description }) {
  return (
    <header className="sc-catalogue-head">
      <p className="sc-eyebrow">{eyebrow}</p>
      <h1>{title}</h1>
      <p>{description}</p>
    </header>
  );
}

export function GuidesCatalogue() {
  return (
    <main className="sc-page">
      <PageMeta
        title="ShakeChillies API Guides | LeadZLab"
        description="Practical ShakeChillies API guides for company enrichment, startup funding tracking, and developer data workflows on RapidAPI."
        path="/shakechillies/guides"
      />
      <Header />
      <section className="sc-catalogue">
        <CatalogueHeader
          eyebrow="Guides"
          title="Practical workflows, explained clearly."
          description="Short tutorials for turning ShakeChillies APIs into useful research, enrichment, and monitoring workflows."
        />
        <div className="sc-guide-grid">
          {guides.map((guide) => (
            <Link className="sc-guide-card" to={guide.href} key={guide.href}>
              <img src={guide.image} alt="" />
              <span>{guide.tag}</span>
              <h2>{guide.title}</h2>
              <p>{guide.description}</p>
              <strong>Read guide →</strong>
            </Link>
          ))}
        </div>
      </section>
      <Footer />
    </main>
  );
}

export function APIsCatalogue() {
  return (
    <main className="sc-page">
      <PageMeta
        title="ShakeChillies API Catalogue | LeadZLab"
        description="Browse focused ShakeChillies APIs for company enrichment, startup funding tracking, and useful B2B data workflows on RapidAPI."
        path="/shakechillies/apis"
      />
      <Header />
      <section className="sc-catalogue">
        <CatalogueHeader
          eyebrow="API catalogue"
          title="Focused APIs for useful B2B data."
          description="Each API is available through RapidAPI, with an interactive playground for testing requests before you integrate."
        />
        <div className="sc-api-catalogue">
          <article className="sc-api-card">
            <div>
              <p className="sc-api-number">01</p>
              <h2>Company profile API</h2>
              <p>
                Enrich a company name with structured fields including its
                website, location, industries, size, description, and optional
                funding data.
              </p>
            </div>
            <div className="sc-card-actions">
              <Link to="/shakechillies/guides/company-profile-enrichment">
                Read the guide
              </Link>
              <a
                className="sc-button primary"
                href={rapidApi.company}
                target="_blank"
                rel="noopener noreferrer"
              >
                View on RapidAPI ↗
              </a>
            </div>
          </article>
          <article className="sc-api-card">
            <div>
              <p className="sc-api-number">02</p>
              <h2>Funding Tracker API</h2>
              <p>
                Find recent startup funding signals and filter structured
                results by stage, country, industry, time period, and other
                useful fields.
              </p>
            </div>
            <div className="sc-card-actions">
              <Link to="/shakechillies/guides/startup-funding-tracker">
                Read the guide
              </Link>
              <a
                className="sc-button primary"
                href={rapidApi.funding}
                target="_blank"
                rel="noopener noreferrer"
              >
                View on RapidAPI ↗
              </a>
            </div>
          </article>
        </div>
      </section>
      <Footer />
    </main>
  );
}

function GuideShell({
  children,
  title,
  metaTitle,
  metaDescription,
  subtitle,
  cover,
  coverAlt,
  apiHref,
  path,
}) {
  const absoluteCover = `${siteUrl}${cover}`;
  return (
    <main className="sc-page">
      <PageMeta
        title={metaTitle}
        description={metaDescription}
        path={path}
        image={absoluteCover}
        type="article"
        jsonLd={articleJsonLd({
          headline: title,
          description: metaDescription,
          image: absoluteCover,
          path,
        })}
      />
      <Header />
      <article className="sc-article">
        <header className="sc-article-header">
          <Link className="sc-back" to="/shakechillies">
            ← All guides
          </Link>
          <p className="sc-eyebrow">ShakeChillies Guide</p>
          <h1>{title}</h1>
          <p className="sc-subtitle">{subtitle}</p>
          <div className="sc-article-meta" aria-label="Article details">
            <span>Practical tutorial</span>
            <span>About 5 minutes</span>
          </div>
        </header>
        <img className="sc-cover" src={cover} alt={coverAlt} />
        <div className="sc-article-body">{children}</div>
        <aside className="sc-resource-box">
          <h2>Try the API used in this guide</h2>
          <p>
            This guide uses a hosted ShakeChillies endpoint available through
            RapidAPI. Review the live playground for the exact request format
            before using it in production.
          </p>
          <a
            className="sc-button primary"
            href={apiHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open RapidAPI listing
          </a>
        </aside>
      </article>
      <Footer />
    </main>
  );
}

export function CompanyProfileGuide() {
  return (
    <GuideShell
      title="How to enrich company profiles from public business data"
      metaTitle="Company Profile Enrichment API Guide | ShakeChillies"
      metaDescription="Learn how to enrich company profiles with structured business data using the ShakeChillies API on RapidAPI. Includes example requests and production tips."
      subtitle="Turn a company name into structured fields your lead research, CRM enrichment, or market-intelligence workflow can actually use."
      cover="/shakechillies/assets/company-profile-enrichment-cover.png"
      coverAlt="Company profile enrichment workflow showing a company name turning into structured profile fields"
      apiHref={rapidApi.company}
      path="/shakechillies/guides/company-profile-enrichment"
    >
      <h2>What this workflow is for</h2>
      <p>
        Company enrichment is useful when you already have a company name and
        need a cleaner profile: what the company does, where it is based, what
        industries it belongs to, its website, size, and sometimes funding
        information.
      </p>
      <ul>
        <li>Enrich company names inside a CRM or lead list.</li>
        <li>Add context to sales or research workflows.</li>
        <li>Normalize messy company records into a consistent schema.</li>
        <li>Store reusable company fields for dashboards or internal tools.</li>
      </ul>

      <h2>Example response shape</h2>
      <pre>{`{
  "company": {
    "about": "Apple is a technology company...",
    "founded_year": "1976-04-01",
    "funding": {
      "currency": "USD",
      "value": 1170230000,
      "value_usd": 1170230000
    },
    "industries": ["Apps", "Information Technology", "Mobile Devices"],
    "location": "Cupertino, California, United States",
    "size": "10001+",
    "website": "https://www.apple.com"
  }
}`}</pre>
      <p>
        Treat funding data as optional. Some company profiles will not include a
        funding object, so production code should never assume it exists.
      </p>

      <h2>Safe JavaScript handling</h2>
      <pre>{`const company = data.company;

const row = {
  website: company.website ?? null,
  location: company.location ?? null,
  industries: company.industries ?? [],
  fundingUsd: company.funding?.value_usd ?? null
};`}</pre>

      <h2>Production notes</h2>
      <ul>
        <li>Cache repeated lookups so you do not enrich the same company again.</li>
        <li>Store the original search input next to the returned profile.</li>
        <li>Log failed lookups for manual review or retry.</li>
        <li>Start with a small batch before running a large enrichment job.</li>
      </ul>
      <p>
        Disclosure: this guide is maintained by ShakeChillies. Product language
        should not be read as an official Crunchbase affiliation unless
        explicitly stated on the RapidAPI listing.
      </p>
      <p>
        Related:{" "}
        <Link to="/shakechillies/guides/startup-funding-tracker">
          Track newly funded startups with the Funding Tracker API
        </Link>
        .
      </p>
    </GuideShell>
  );
}

export function FundingTrackerGuide() {
  return (
    <GuideShell
      title="How to track newly funded startups in 10 minutes"
      metaTitle="Startup Funding Tracker API Guide | ShakeChillies"
      metaDescription="Learn how to track newly funded startups with the ShakeChillies Funding Tracker API on RapidAPI. Includes filters, examples, and workflow ideas."
      subtitle="Use funding signals to build a lightweight prospecting, research, or market-monitoring workflow."
      cover="/shakechillies/assets/funding-tracker-cover.png"
      coverAlt="Startup funding tracker workflow showing recently funded companies becoming a lead list"
      apiHref={rapidApi.funding}
      path="/shakechillies/guides/startup-funding-tracker"
    >
      <h2>What this workflow is for</h2>
      <p>
        Recently funded startups can be useful signals for sales teams,
        researchers, investors, recruiters, and operators. The goal is not just
        to read funding news; it is to turn funding activity into structured
        data you can filter and act on.
      </p>
      <ul>
        <li>Find startups that recently raised funding.</li>
        <li>Filter by funding stage, country, industry, or time period.</li>
        <li>Build a weekly funding digest or prospecting list.</li>
        <li>Export structured rows into a CRM, spreadsheet, or research tool.</li>
      </ul>

      <h2>Useful endpoints</h2>
      <ul>
        <li>
          <code>GET /funding/search</code> — search funding rounds with filters.
        </li>
        <li>
          <code>GET /funding/enums</code> — fetch allowed values for filters.
        </li>
      </ul>

      <h2>Example workflow</h2>
      <ol>
        <li>Fetch allowed filters from the enums endpoint.</li>
        <li>Search recent rounds for a narrow time window.</li>
        <li>Filter by stage, country, or industry.</li>
        <li>Map results into clean rows.</li>
        <li>Export or push the list into your next tool.</li>
      </ol>

      <pre>{`const rounds = Array.isArray(data.results) ? data.results : [];

const rows = rounds.map((round) => ({
  company: round.company ?? "Unknown company",
  stage: round.stage ?? null,
  amount: round.amount ?? null,
  date: round.date ?? null,
  investors: round.investors ?? [],
  website: round.website ?? null,
  linkedinUrl: round.linkedin_url ?? null
}));`}</pre>

      <h2>Production notes</h2>
      <ul>
        <li>Start with narrow filters before running broad searches.</li>
        <li>Treat amount, investors, website, and LinkedIn URL as optional.</li>
        <li>Store the date when you fetched the data.</li>
        <li>Review results before triggering automated outreach.</li>
      </ul>
      <p>
        Disclosure: this guide is maintained by ShakeChillies. Sample results
        are illustrative; supported fields and filters should be checked against
        the current RapidAPI playground.
      </p>
      <p>
        Related:{" "}
        <Link to="/shakechillies/guides/company-profile-enrichment">
          Enrich company profiles with structured business data
        </Link>
        .
      </p>
    </GuideShell>
  );
}
